import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Bell,
  Check,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Square,
  Clock,
  MessageSquare,
  Eye,
  EyeOff,
  Mail,
  X,
} from 'lucide-react';
import {
  fetchAlerts,
  acknowledgeAlert,
  updateDisputeStatus,
  snoozeAlert,
  batchAcknowledge,
  batchSnooze,
  batchDispute,
} from '../api/alerts';
import { fetchProjects } from '../api/projects';
import { formatRelativeDate, formatDateWithTooltip } from '../utils/dateUtils';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/Button';

const alertTypeLabels = {
  link_removed: { label: 'Link Removed', color: 'bg-destructive/20 text-destructive border-destructive/30' },
  nofollow_added: { label: 'Nofollow Added', color: 'bg-warning/20 text-warning border-warning/30' },
  redirect: { label: 'Redirect', color: 'bg-warning/20 text-warning border-warning/30' },
  page_404: { label: '404 Error', color: 'bg-destructive/20 text-destructive border-destructive/30' },
  anchor_changed: { label: 'Anchor Changed', color: 'bg-info/20 text-info border-info/30' },
  da_drop: { label: 'DA Dropped', color: 'bg-warning/20 text-warning border-warning/30' },
  link_restored: { label: 'Link Restored', color: 'bg-success/20 text-success border-success/30' },
  target_url_changed: { label: 'Target URL Changed', color: 'bg-warning/20 text-warning border-warning/30' },
  link_type_changed: { label: 'Link Type Changed', color: 'bg-info/20 text-info border-info/30' },
  link_position_changed: { label: 'Position Changed', color: 'bg-info/20 text-info border-info/30' },
  page_noindex: { label: 'Page Noindexed', color: 'bg-destructive/20 text-destructive border-destructive/30' },
};

const disputeStatusLabels = {
  contacted: { label: 'Contacted', color: 'bg-info/20 text-info' },
  waiting: { label: 'Waiting', color: 'bg-warning/20 text-warning' },
  resolved: { label: 'Resolved', color: 'bg-success/20 text-success' },
  ignored: { label: 'Ignored', color: 'bg-muted text-muted-foreground' },
};

function Alerts() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Read filters from URL params
  const filters = {
    project_id: searchParams.get('project_id') || '',
    type: searchParams.get('type') || '',
    dispute_status: searchParams.get('dispute_status') || '',
    show_snoozed: searchParams.get('show_snoozed') || '',
    page: parseInt(searchParams.get('page') || '1', 10),
  };

  const setFilters = (newFilters) => {
    const params = new URLSearchParams();
    if (newFilters.project_id) params.set('project_id', newFilters.project_id);
    if (newFilters.type) params.set('type', newFilters.type);
    if (newFilters.dispute_status) params.set('dispute_status', newFilters.dispute_status);
    if (newFilters.show_snoozed) params.set('show_snoozed', newFilters.show_snoozed);
    if (newFilters.page > 1) params.set('page', newFilters.page.toString());
    setSearchParams(params, { replace: true });
  };

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: fetchProjects,
  });

  const { data: alertsData, isLoading } = useQuery({
    queryKey: ['alerts', filters],
    queryFn: () => fetchAlerts(filters),
  });

  const ackMutation = useMutation({
    mutationFn: acknowledgeAlert,
    onSuccess: () => {
      queryClient.invalidateQueries(['alerts']);
      toast.success('Alert acknowledged');
    },
  });

  const disputeMutation = useMutation({
    mutationFn: ({ id, dispute_status, notes }) => updateDisputeStatus(id, { dispute_status, notes }),
    onSuccess: () => {
      queryClient.invalidateQueries(['alerts']);
      toast.success('Dispute status updated');
    },
  });

  const snoozeMutation = useMutation({
    mutationFn: ({ id, days }) => snoozeAlert(id, days),
    onSuccess: () => {
      queryClient.invalidateQueries(['alerts']);
      toast.success('Alert snoozed');
    },
  });

  const batchAckMutation = useMutation({
    mutationFn: batchAcknowledge,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['alerts']);
      setSelectedIds(new Set());
      toast.success(`Acknowledged ${data.updated} alerts`);
    },
  });

  const batchSnoozeMutation = useMutation({
    mutationFn: ({ ids, days }) => batchSnooze(ids, days),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['alerts']);
      setSelectedIds(new Set());
      toast.success(`Snoozed ${data.updated} alerts`);
    },
  });

  const batchDisputeMutation = useMutation({
    mutationFn: ({ ids, dispute_status }) => batchDispute(ids, { dispute_status }),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['alerts']);
      setSelectedIds(new Set());
      toast.success(`Updated ${data.updated} alerts`);
    },
  });

  const projects = projectsData?.projects || [];
  const alertsList = alertsData?.alerts || [];
  const pagination = alertsData?.pagination || { page: 1, pages: 1, total: 0 };
  const someSelected = selectedIds.size > 0;
  const allSelected = alertsList.length > 0 && alertsList.every(a => selectedIds.has(a.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(alertsList.map(a => a.id)));
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Alerts</h1>
          <p className="text-muted-foreground mt-1">
            Monitor changes to your backlinks
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <select
          value={filters.project_id}
          onChange={(e) => setFilters({ ...filters, project_id: e.target.value, page: 1 })}
          className="px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm focus:ring-2 focus:ring-ring"
        >
          <option value="">All Projects</option>
          {projects.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
        <select
          value={filters.type}
          onChange={(e) => setFilters({ ...filters, type: e.target.value, page: 1 })}
          className="px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm focus:ring-2 focus:ring-ring"
        >
          <option value="">All Types</option>
          {Object.entries(alertTypeLabels).map(([type, config]) => (
            <option key={type} value={type}>{config.label}</option>
          ))}
        </select>
        <select
          value={filters.dispute_status}
          onChange={(e) => setFilters({ ...filters, dispute_status: e.target.value, page: 1 })}
          className="px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm focus:ring-2 focus:ring-ring"
        >
          <option value="">All Statuses</option>
          <option value="null">No dispute</option>
          {Object.entries(disputeStatusLabels).map(([status, config]) => (
            <option key={status} value={status}>{config.label}</option>
          ))}
        </select>
        <button
          onClick={() => setFilters({ ...filters, show_snoozed: filters.show_snoozed ? '' : '1', page: 1 })}
          className={cn(
            'px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors border',
            filters.show_snoozed
              ? 'bg-warning/20 text-warning border-warning/30'
              : 'bg-card text-muted-foreground border-border hover:text-foreground'
          )}
        >
          {filters.show_snoozed ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          {filters.show_snoozed ? 'Showing snoozed' : 'Show snoozed'}
        </button>
      </div>

      {/* Bulk Action Bar */}
      {someSelected && (
        <div className="flex items-center gap-3 p-3 bg-card/50 border border-border rounded-xl">
          <div className="flex items-center gap-2 text-primary text-sm font-medium">
            <CheckSquare className="h-4 w-4" />
            {selectedIds.size} selected
          </div>
          <div className="flex-1 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={() => batchAckMutation.mutate(Array.from(selectedIds))}
              loading={batchAckMutation.isPending}
              leftIcon={<Check className="h-3.5 w-3.5" />}
            >
              Acknowledge All
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => batchSnoozeMutation.mutate({ ids: Array.from(selectedIds), days: 7 })}
              loading={batchSnoozeMutation.isPending}
              leftIcon={<Clock className="h-3.5 w-3.5" />}
            >
              Snooze 7d
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => batchDisputeMutation.mutate({ ids: Array.from(selectedIds), dispute_status: 'contacted' })}
              loading={batchDisputeMutation.isPending}
              leftIcon={<MessageSquare className="h-3.5 w-3.5" />}
            >
              Mark Contacted
            </Button>
          </div>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="p-1.5 text-muted-foreground hover:text-foreground rounded-lg transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Alerts List */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-24 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      ) : alertsList.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 border border-dashed border-border rounded-xl">
          <Bell className="h-16 w-16 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            No alerts yet
          </h3>
          <p className="text-muted-foreground text-center max-w-md">
            When your backlinks change status, alerts will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Select all */}
          <div className="flex items-center gap-2 px-1">
            <button onClick={toggleSelectAll} className="p-1 text-muted-foreground hover:text-foreground">
              {allSelected ? <CheckSquare className="h-5 w-5 text-primary" /> : <Square className="h-5 w-5" />}
            </button>
            <span className="text-xs text-muted-foreground">Select all</span>
          </div>

          {alertsList.map((alert) => {
            const typeConfig = alertTypeLabels[alert.type] || {
              label: alert.type,
              color: 'bg-muted text-muted-foreground',
            };
            const disputeConfig = alert.dispute_status ? disputeStatusLabels[alert.dispute_status] : null;
            const isSnoozed = alert.snoozed_until && new Date(alert.snoozed_until) > new Date();

            return (
              <div
                key={alert.id}
                className={cn(
                  'rounded-xl border bg-card p-4 hover:border-border/80 transition-colors',
                  selectedIds.has(alert.id) ? 'border-primary/50 bg-primary/5' : 'border-border',
                  isSnoozed && 'opacity-60'
                )}
              >
                <div className="flex items-start gap-3">
                  {/* Checkbox */}
                  <button
                    onClick={() => toggleSelect(alert.id)}
                    className="mt-1 p-0.5 text-muted-foreground hover:text-foreground flex-shrink-0"
                  >
                    {selectedIds.has(alert.id) ? (
                      <CheckSquare className="h-5 w-5 text-primary" />
                    ) : (
                      <Square className="h-5 w-5" />
                    )}
                  </button>

                  {/* Type badge + dispute status */}
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${typeConfig.color}`}>
                      {typeConfig.label}
                    </span>
                    {disputeConfig && (
                      <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium ${disputeConfig.color}`}>
                        {disputeConfig.label}
                      </span>
                    )}
                    {isSnoozed && (
                      <span className="px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-muted text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Snoozed
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                      <span>{alert.project_name}</span>
                      <span className="text-muted-foreground/40">|</span>
                      <span title={formatDateWithTooltip(alert.created_at).absolute}>
                        {formatRelativeDate(alert.created_at)}
                      </span>
                    </div>
                    {alert.backlink_url && (
                      <Link
                        to={`/backlinks/${alert.backlink_id}`}
                        className="text-foreground hover:text-primary transition-colors block truncate text-sm"
                        title={alert.backlink_url}
                      >
                        {alert.backlink_url}
                      </Link>
                    )}
                    {alert.old_value && alert.new_value && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {alert.old_value} &rarr; {alert.new_value}
                      </p>
                    )}
                    {alert.notes && (
                      <p className="text-xs text-muted-foreground/80 mt-1 italic">
                        Note: {alert.notes}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!alert.sent && (
                      <button
                        onClick={() => ackMutation.mutate(alert.id)}
                        className="p-1.5 text-muted-foreground hover:text-success hover:bg-success/10 rounded-lg transition-colors"
                        title="Acknowledge"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                    )}

                    {/* Snooze dropdown */}
                    <div className="relative group">
                      <button
                        className="p-1.5 text-muted-foreground hover:text-warning hover:bg-warning/10 rounded-lg transition-colors"
                        title="Snooze"
                      >
                        <Clock className="h-4 w-4" />
                      </button>
                      <div className="absolute right-0 top-full mt-1 py-1 bg-card border border-border rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20 min-w-[120px]">
                        <button
                          onClick={() => snoozeMutation.mutate({ id: alert.id, days: 7 })}
                          className="w-full px-3 py-1.5 text-left text-sm text-foreground hover:bg-muted transition-colors"
                        >
                          7 days
                        </button>
                        <button
                          onClick={() => snoozeMutation.mutate({ id: alert.id, days: 14 })}
                          className="w-full px-3 py-1.5 text-left text-sm text-foreground hover:bg-muted transition-colors"
                        >
                          14 days
                        </button>
                      </div>
                    </div>

                    {/* Dispute dropdown */}
                    <div className="relative group">
                      <button
                        className="p-1.5 text-muted-foreground hover:text-info hover:bg-info/10 rounded-lg transition-colors"
                        title="Set dispute status"
                      >
                        <MessageSquare className="h-4 w-4" />
                      </button>
                      <div className="absolute right-0 top-full mt-1 py-1 bg-card border border-border rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20 min-w-[140px]">
                        {Object.entries(disputeStatusLabels).map(([status, config]) => (
                          <button
                            key={status}
                            onClick={() => disputeMutation.mutate({ id: alert.id, dispute_status: status })}
                            className="w-full px-3 py-1.5 text-left text-sm text-foreground hover:bg-muted transition-colors"
                          >
                            {config.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <Link
                      to={`/backlinks/${alert.backlink_id}`}
                      className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-muted-foreground">
                Showing {((pagination.page - 1) * 50) + 1} to{' '}
                {Math.min(pagination.page * 50, pagination.total)} of {pagination.total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                  disabled={filters.page === 1}
                  className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-50"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                  disabled={filters.page >= pagination.pages}
                  className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-50"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Alerts;
